import React from 'react'
import { observer } from 'mobx-react'
import cx from 'classnames'
import styles from './DuplicateBundleViewModel.sass'
import DuplicateBundleViewModel from './DuplicateBundleViewModel'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
  ModalSheetSection,
} from 'components/ModalSheet'
import {
  FormColumn,
  FormColumns,
  FormRow,
  TextInputContainer,
  Form,
  NumericInputContainer,
  TextAreaContainer,
  FormGroupContainer,
} from 'components/inputs'
import { IncludedItemViewModel } from 'admin/bundle/components/ManageItemsInBundleViewModel/ManageItemsInBundleViewModel'
import { IconButton } from 'components/IconButton'
import Icon from 'components/Icon'
import { PriceEditorViewModel } from '../PriceEditorViewModel'
import ManageItemsInBundleViewModel from 'admin/bundle/components/ManageItemsInBundleViewModel'
import Button from 'components/Button'
import BundleFormWorkpaceDropdown from 'admin/bundle/components/BundleFormWorkpaceDropdown'

interface Props {
  vm: DuplicateBundleViewModel
}

function DuplicateBundleDialog({ vm }: Props) {
  const isRelevantIncludedItemViewModelsEmpty = vm.includedItems.length === 0

  return (
    <ModalSheet
      disableCloseOnOutsideClick
      showing={vm.showing}
      onRequestClose={vm.close}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Duplicate bundle</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetSection spaced>
          <Form id="addItemForm" onSubmit={vm.submit}>
            <BundleFormWorkpaceDropdown
              vm={vm}
              workspaceSelectorVM={vm.workspaceSelectorVM}
            />
            <FormRow>
              <TextInputContainer
                autoFocus
                label="Bundle name"
                field="name"
                model={vm}
                error={vm.validation.getError('name')}
                data-test="bundle-modal-name-input"
                help={vm.remainingCharactersIndicator.name}
              />
            </FormRow>

            <FormRow>
              <TextAreaContainer
                rows={4}
                label="Description"
                field="description"
                minLength={1}
                maxLength={280}
                help={vm.remainingCharactersIndicator.description}
                model={vm}
                error={vm.validation.getError('description')}
                data-test="bundle-modal-description-input"
              />
            </FormRow>

            <PriceInput vm={vm.pricingVM} />

            <FormRow>
              <FormGroupContainer>
                <label className={styles.includedItemsLabel}>
                  <div>
                    Service items
                    <span className={styles.includedItemCount}>
                      ({vm.includedItems.length || 0})
                    </span>
                  </div>
                  {!isRelevantIncludedItemViewModelsEmpty && (
                    <div className={styles.includedItemQuantityLabel}>
                      Included quantity
                    </div>
                  )}
                </label>
                <div>
                  {isRelevantIncludedItemViewModelsEmpty && <NoIncludedItems />}
                  {vm.orderedIncludedItems.map((item, index) => (
                    <IncludedItem key={index} includedItem={item} />
                  ))}
                </div>
                <Button
                  onClick={vm.manageItemsInBundleViewModel.show}
                  className={styles.includedItemRowManageButton}
                  type="text"
                  size="normal"
                  disabled={!vm.manageItemsInBundleViewModel.isWorkspaceIdSet}
                  dataTest="bundle-modal-manage-included-items-button"
                >
                  Manage service items
                </Button>
              </FormGroupContainer>
            </FormRow>
          </Form>
        </ModalSheetSection>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            dataTest="bundle-modal-close-button"
            onClick={vm.close}
            type="default"
          >
            Close
          </Button>

          <Button
            submit
            form="addItemForm"
            type="primary"
            loading={vm.submit.pending}
            disabled={vm.submit.pending}
            dataTest="bundle-modal-submit-button"
          >
            Save
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
      <ManageItemsInBundleViewModel vm={vm.manageItemsInBundleViewModel} />
    </ModalSheet>
  )
}

export default observer(DuplicateBundleDialog)

const PriceInput = observer(function ({ vm }: { vm: PriceEditorViewModel }) {
  return (
    <FormRow>
      <FormColumns>
        <FormColumn>
          <NumericInputContainer
            precision={2}
            label="Client price ($)"
            field="price"
            model={vm}
            error={vm.validation.getError('price')}
            data-test="bundle-modal-price-input"
            help="The amount to charge the client for all the items in the bundle."
          />
        </FormColumn>
      </FormColumns>
    </FormRow>
  )
})

interface IncludedItemProps {
  includedItem: IncludedItemViewModel
}

const IncludedItem = observer(function ({ includedItem }: IncludedItemProps) {
  const isQuantityChangeDisabled =
    !includedItem.isIncludedItem || includedItem.isBaseItem
  return (
    <div className={styles.includedItem}>
      <div className={styles.includedItemInfo}>
        <div className={styles.itemHeader}>
          <span>{includedItem.name}</span>
          {includedItem.isBaseItem && (
            <span className={styles.baseItem}>* base item</span>
          )}
        </div>
        <div className={styles.includedDescription}>
          {includedItem.description}
        </div>
      </div>
      <div className={styles.itemQuantityContainer}>
        <div
          className={cx(
            styles.itemQuantityBox,
            isQuantityChangeDisabled && styles.isDisabled,
          )}
        >
          <IconButton
            onClick={includedItem.decrementQuantity}
            disabled={isQuantityChangeDisabled}
          >
            <Icon name="subtract" size={16} />
          </IconButton>

          <input
            key="quantity"
            type="number"
            min="1"
            className={cx(styles.input, styles.itemQuantity)}
            value={includedItem.includedQuantity}
            onChange={(e) => includedItem.setQuantity(e.target.value)}
            data-test={`bundle-modal-included-quantity-${includedItem.name}`}
            disabled={isQuantityChangeDisabled}
          />

          <IconButton
            onClick={includedItem.incrementQuantity}
            disabled={isQuantityChangeDisabled}
          >
            <Icon name="add" size={16} />
          </IconButton>
        </div>
      </div>
    </div>
  )
})

const NoIncludedItems = observer(function () {
  return (
    <div className={cx(styles.includedItem, styles.noneAssigned)}>
      None assigned
    </div>
  )
})
